import { useCallback, useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import {
  ApiError,
  getQuestionText,
  postAnswer,
  postFinalize,
  postSkip,
} from '../lib/api';
import ProgressBar from './ProgressBar';
import QuestionCard from './QuestionCard';
import PttButton from './PttButton';
import ErrorScreen from './ErrorScreen';
import Button from './ui/Button';
import { Card, CardContent } from './ui/Card';
import Shell from './ui/Shell';

interface InterviewSessionProps {
  token: string;
  startIdx: number;
  numQuestions: number;
  stream: MediaStream;
  onCompleted: () => void;
}

type Phase =
  | { kind: 'loading-question' }
  | { kind: 'audio-playing' }
  | { kind: 'awaiting-answer' }
  | { kind: 'submitting' }
  | { kind: 'finalizing' }
  | { kind: 'error'; message: string; recoverable: boolean };

export default function InterviewSession({
  token,
  startIdx,
  numQuestions,
  stream,
  onCompleted,
}: InterviewSessionProps) {
  const [idx, setIdx] = useState(startIdx);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading-question' });
  const [answeredCount, setAnsweredCount] = useState(0);
  const [showAck, setShowAck] = useState(false);
  const finalizingRef = useRef(false);

  const loadQuestion = useCallback(
    async (which: number) => {
      setPhase({ kind: 'loading-question' });
      try {
        await getQuestionText(token, which);
        setPhase({ kind: 'audio-playing' });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Could not load the next question.';
        const recoverable = !(err instanceof ApiError && err.isExpired);
        setPhase({ kind: 'error', message, recoverable });
      }
    },
    [token],
  );

  useEffect(() => {
    void loadQuestion(idx);
  }, [idx, loadQuestion]);

  const finalize = useCallback(async () => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setPhase({ kind: 'finalizing' });
    try {
      await postFinalize(token);
      onCompleted();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not finish the interview.';
      finalizingRef.current = false;
      setPhase({ kind: 'error', message, recoverable: true });
    }
  }, [token, onCompleted]);

  const advanceTo = useCallback(
    async (next: number, isLast: boolean) => {
      if (isLast || next > numQuestions) {
        await finalize();
        return;
      }
      setIdx(next);
    },
    [finalize, numQuestions],
  );

  const handleAnswer = useCallback(
    async (blob: Blob) => {
      setPhase({ kind: 'submitting' });
      try {
        const res = await postAnswer(token, idx, blob);
        setAnsweredCount((n) => n + 1);
        setShowAck(true);
        window.setTimeout(() => setShowAck(false), 800);
        await advanceTo(res.next_idx, res.is_last);
      } catch (err) {
        if (err instanceof ApiError && err.isModelsLoading) {
          setPhase({
            kind: 'error',
            message: 'The transcription server is starting up. Please wait a moment and try again.',
            recoverable: true,
          });
          throw err;
        }
        const message =
          err instanceof Error ? err.message : 'Could not save your answer.';
        setPhase({ kind: 'error', message, recoverable: true });
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [advanceTo, idx, token],
  );

  const handleSkip = useCallback(async () => {
    if (!window.confirm('Skip this question? You will not be able to come back to it.')) {
      return;
    }
    setPhase({ kind: 'submitting' });
    try {
      const res = await postSkip(token, idx);
      await advanceTo(res.next_idx, res.is_last);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not skip the question.';
      setPhase({ kind: 'error', message, recoverable: true });
    }
  }, [advanceTo, idx, token]);

  const handleFinishEarly = useCallback(async () => {
    if (
      !window.confirm(
        'End the interview now? Your recruiter will only see the questions you have already answered.',
      )
    ) {
      return;
    }
    await finalize();
  }, [finalize]);

  if (phase.kind === 'error') {
    return (
      <ErrorScreen
        message={phase.message}
        onRetry={
          phase.recoverable
            ? () => {
                finalizingRef.current = false;
                void loadQuestion(idx);
              }
            : undefined
        }
        hint={
          phase.recoverable
            ? 'Your previous answers are safe — only this question needs to be retried.'
            : 'Please contact your recruiter to get a new interview link.'
        }
      />
    );
  }

  const pttDisabled =
    phase.kind === 'loading-question' ||
    phase.kind === 'audio-playing' ||
    phase.kind === 'submitting' ||
    phase.kind === 'finalizing';

  let pttDisabledReason: string | undefined;
  if (phase.kind === 'audio-playing') pttDisabledReason = 'Listening to question…';
  else if (phase.kind === 'loading-question') pttDisabledReason = 'Loading next question…';
  else if (phase.kind === 'submitting') pttDisabledReason = 'Saving answer…';
  else if (phase.kind === 'finalizing') pttDisabledReason = 'Wrapping up…';

  return (
    <Shell maxWidth="wide">
      <Card>
        <CardContent>
          <header className="mb-5 flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Question {idx} of {numQuestions}
            </span>
            <ProgressBar current={idx} total={numQuestions} />
          </header>

          {(phase.kind === 'audio-playing' || phase.kind === 'awaiting-answer') && (
            <QuestionCard
              token={token}
              idx={idx}
              onAudioStart={() =>
                setPhase((p) =>
                  p.kind === 'audio-playing' ? p : { kind: 'audio-playing' },
                )
              }
              onAudioEnded={() =>
                setPhase((p) =>
                  p.kind === 'awaiting-answer' ? p : { kind: 'awaiting-answer' },
                )
              }
            />
          )}

          {phase.kind === 'loading-question' && (
            <div className="px-4 py-8 text-center text-muted-foreground">
              Loading next question…
            </div>
          )}
          {phase.kind === 'finalizing' && (
            <div className="px-4 py-8 text-center text-muted-foreground">
              Wrapping up your interview…
            </div>
          )}

          <div className="flex justify-center">
            <PttButton
              stream={stream}
              disabled={pttDisabled}
              {...(pttDisabledReason ? { disabledReason: pttDisabledReason } : {})}
              onStop={handleAnswer}
            />
          </div>

          {showAck && (
            <div
              className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full bg-billed-soft px-3 py-1.5 text-sm text-billed"
              role="status"
              aria-live="polite"
            >
              <Check className="h-4 w-4" strokeWidth={2.5} />
              Answer received.
            </div>
          )}

          <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4 text-sm">
            <Button
              variant="link"
              onClick={handleSkip}
              disabled={pttDisabled}
            >
              Skip this question
            </Button>
            {answeredCount > 0 && (
              <Button
                variant="linkMuted"
                onClick={handleFinishEarly}
                disabled={pttDisabled}
              >
                Finish interview early
              </Button>
            )}
          </footer>
        </CardContent>
      </Card>
    </Shell>
  );
}
